import { z } from "zod";
import {
  MatchingPipelineRunIdSchema,
  EventIdSchema,
  NO_PERSON_ID,
  PersonIdSchema,
} from "./branded-id.schemas.js";
import { FamiliarityReconnectionSchema } from "./candidate-pool-data.schemas.js";

export const PortfolioRole = {
  Opportunity: "Opportunity",
  Learning: "Learning",
  Surprise: "Surprise",
  SimilarChapter: "Similar Chapter",
  Bridge: "Bridge",
} as const;

export const portfolioRoleValues = [
  PortfolioRole.Opportunity,
  PortfolioRole.Learning,
  PortfolioRole.Surprise,
  PortfolioRole.SimilarChapter,
  PortfolioRole.Bridge,
] as const;

export const ConvictionLevel = {
  must_meet: "must_meet",
  strong: "strong",
  worth_considering: "worth_considering",
} as const;

export const convictionLevelValues = [
  ConvictionLevel.must_meet,
  ConvictionLevel.strong,
  ConvictionLevel.worth_considering,
] as const;

export type ConvictionLevelType = (typeof convictionLevelValues)[number];

const BILATERAL_TIER_VALUES = ["observed", "inferred", "assumed"] as const;

export const BilateralGetsSchema = z.object({
  text: z.string(),
  tier: z.enum(BILATERAL_TIER_VALUES),
});

export type BilateralGets = Readonly<z.infer<typeof BilateralGetsSchema>>;

export const MatchSelectionSchema = z.object({
  candidateIndex: z.number(),
  name: z.string(),
  personId: PersonIdSchema.optional().default(NO_PERSON_ID),
  portfolioRole: z.enum(portfolioRoleValues),
  connectionDimensions: z.array(z.string()).optional().default([]),
  conviction: z.enum(convictionLevelValues),
  connectionAnalysis: z.string().optional().default(""),
  readerGets: BilateralGetsSchema,
  candidateGets: BilateralGetsSchema,
  conversationAngles: z.array(z.string()).optional().default([]),
  isReconnection: z.boolean().optional().default(false),
  reconnectionContext: z.string().nullable().optional().default(null),
});

export type MatchSelection = Readonly<z.infer<typeof MatchSelectionSchema>>;

export const SelectionResultSchema = z.object({
  selections: z.array(MatchSelectionSchema),
  substitutes: z.array(MatchSelectionSchema),
});

export type SelectionResult = Readonly<z.infer<typeof SelectionResultSchema>>;

const PreselectionHintsSchema = z.object({
  sharedMarkers: z.array(z.string()),
  clusters: z.array(z.object({ lens: z.string(), name: z.string() })),
  transactional: z
    .object({
      targetType: z.string(),
      candidateType: z.string(),
      /**
       * Which two asks made the deal, as indices into each person's
       * `personCoreBrief.data.reasonsToMeet` (PLA-1786). The reader resolves the
       * ask from the brief it already holds rather than from a copy stored here.
       *
       * An identity, not a rank: the brief emits its reasons in no meaningful
       * order, so index 0 is not "their most important ask".
       *
       * Optional because the hint is older than they are: picks written before
       * PLA-1786 carry a `transactional` naming the two types and nothing else,
       * and requiring the indices made every one of those docs undecodable. The
       * writer still emits the hint only when it has both, so a fresh pick that
       * names a deal always names its asks.
       */
      targetReasonIndex: z.number().int().optional(),
      candidateReasonIndex: z.number().int().optional(),
    })
    .nullable(),
  isDistribution: z.boolean(),
  geoProximity: z.object({ sharedMetro: z.string() }).nullable(),
  tier: z.string(),
  relationshipZone: z.string().optional(),
  /**
   * The institution the pair was inside at the same time, straight off the pool's
   * familiarity signal. Present so the writer is TOLD a suggestion is a
   * reintroduction: before this the model had to notice a shared employer by reading
   * two career blocks, and when it did the pick still said `isReconnection: false`
   * (PLA-1419).
   */
  reconnection: FamiliarityReconnectionSchema.nullish(),
});

export type PreselectionHints = Readonly<
  z.infer<typeof PreselectionHintsSchema>
>;

/**
 * The free-form prose arm of why-connect: one short paragraph replacing the
 * headline + bullets at render time.
 *
 * Carries no verdict of its own. The `gradeProse` detectors it trips are recorded by
 * `pickParagraphCritique`, which judges every generation attempt from one place —
 * a generator that grades its own homework is how the card gate came to grade a
 * draft 87% of picks had already replaced (PLA-1344).
 */
export const WhyConnectParagraphContentSchema = z.object({
  paragraph: z.string().min(1),
});

export type WhyConnectParagraphContent = Readonly<
  z.infer<typeof WhyConnectParagraphContentSchema>
>;

export const PickWhyConnectParagraphEntrySchema =
  WhyConnectParagraphContentSchema.extend({
    candidatePersonId: PersonIdSchema,
    /**
     * Digest of everything the paragraph was written from EXCEPT the output
     * language, so a run that has to redo a reader can tell which of their picks
     * actually moved instead of re-writing the whole deck.
     *
     * Wider than S6's equivalent, which digests the pick alone: the paragraph also
     * reads the candidate's career and brief, arriving through a hash-excluded
     * cohort map. Note what that does and does not buy. On a row that misses for
     * any reason, this digest catches candidate drift no cache key covers. It does
     * NOT make a candidate's change propagate by itself — the reader's row is then
     * a hit, the body never runs, and the digest is never consulted.
     *
     * Optional: entries written before salvage existed carry none and are never
     * reused.
     */
    inputHash: z.string().optional(),
    /**
     * Why this prose is the prose that ships. Present only on the `selected` row —
     * an attempt row is a generation, not a decision.
     *
     * Recorded rather than left derivable: the comparator's verdict is the one thing a
     * reviewer cannot reconstruct from the attempts, because the rule that decided it
     * (a severity trade, an unanswered critic, a promotion regression) is invisible in
     * the two paragraphs it chose between. It used to be computed and thrown away, so
     * "why did it keep the worse-looking one?" had no answer anywhere (PLA-1359).
     */
    selection: z
      .object({
        /** `targetId` of the attempt that shipped; `null` is the base attempt. */
        keptAttempt: z.string().nullable(),
        reason: z.string(),
      })
      .optional(),
  });

export type PickWhyConnectParagraphEntry = Readonly<
  z.infer<typeof PickWhyConnectParagraphEntrySchema>
>;

export const PickSchema = z.object({
  candidateIndex: z.number(),
  name: z.string(),
  personId: PersonIdSchema,
  portfolioRole: z.enum(portfolioRoleValues),
  conviction: z.enum(convictionLevelValues),
  readerGets: BilateralGetsSchema,
  candidateGets: BilateralGetsSchema,
  connectionAnalysis: z.string().optional().default(""),
  conversationAngles: z.array(z.string()).optional().default([]),
  isReconnection: z.boolean(),
  reconnectionContext: z.string().nullable().default(null),
  connectionDimensions: z.array(z.string()).optional().default([]),
  status: z.enum(["active", "substitute"]),
  preselectionHints: PreselectionHintsSchema.optional(),

  whyConnectParagraph: WhyConnectParagraphContentSchema.optional(),
});

export type MatchPick = Readonly<z.infer<typeof PickSchema>>;

export const PickSetSchema = z.object({
  picks: z.array(PickSchema),
  substitutes: z.array(PickSchema),
});

export type PickSet = Readonly<z.infer<typeof PickSetSchema>>;

/**
 * A pick as the engine PUBLISHES it, which is not the shape it persists.
 *
 * The paragraph is required here and optional on {@link PickSchema}, and that gap is
 * the point. The base `pick` doc is minted before the prose arm runs, so at rest a pick
 * legitimately has none yet; by the time one is offered to anybody it must have one,
 * because a suggestion carrying no reason to act on it is worse than one fewer
 * suggestion. `foldPickState` is the boundary that enforces it, so "no why, no
 * suggestion" is a type every consumer inherits rather than a rule each has to
 * remember (PLA-1359).
 *
 * Substitutes keep the persisted shape: they are the bench and are never offered.
 */
export const PublishedPickSchema = PickSchema.extend({
  whyConnectParagraph: WhyConnectParagraphContentSchema,
});

export type PublishedPick = Readonly<z.infer<typeof PublishedPickSchema>>;

export const PublishedPickSetSchema = z.object({
  picks: z.array(PublishedPickSchema),
  substitutes: z.array(PickSchema),
});
export const BackendPickDataSchema = z.object({
  id: z.string(),
  runId: MatchingPipelineRunIdSchema,
  eventId: EventIdSchema,
  personId: PersonIdSchema,
  data: PickSetSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type BackendPickData = Readonly<z.infer<typeof BackendPickDataSchema>>;

/** {@link BackendPickData} after the fold: every active pick carries its paragraph. */
export const PublishedPickDataSchema = BackendPickDataSchema.extend({
  data: PublishedPickSetSchema,
});

export type PublishedPickData = Readonly<
  z.infer<typeof PublishedPickDataSchema>
>;
